import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Calendar } from "react-big-calendar";
import moment from "moment-timezone";
import LessonWrapper from "./LessonWrapper";
import { WEEKDAYS } from '../common/Const';
import { calendarLocalizer, calendarFormats, calendarMessages } from "./calendarViews";
import { FiveDayWeek, SixDayWeek } from "./ShortWeekView";
import {
  DEFAULT_LESSON_TYPES_SETTING,
  getLessonTypeById,
  normalizeLessonTypesSetting,
} from "../common/lessonTypes";
import {
  hasAllParticipantsCancelled,
  isAttendanceChecked,
  isLessonCancelled,
} from "./lessonAttendanceStatus";

import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/sass/styles.scss";

moment.tz.setDefault("Europe/Warsaw");
const views = { month: true, week5: FiveDayWeek, week6: SixDayWeek, week: true, day: true };
const ATTENDED_EVENT_BACKGROUND = "#0c4946";
const CANCELLED_EVENT_BACKGROUND = "#6c757d";
const PARTIALLY_CANCELLED_EVENT_BACKGROUND = "#856404";

function DashboardCalendarEvent({ event }) {
  const { calendarContext } = event;

  return (
    <LessonWrapper
      event={event}
      display="calendar-event"
      students={calendarContext.students}
      teachers={calendarContext.teachers}
      onSubmit={calendarContext.onSubmit}
      deleteLessonRegister={calendarContext.deleteLessonRegister}
      role={calendarContext.userRole}
      userTeacherId={calendarContext.userTeacherId}
    />
  );
}

DashboardCalendarEvent.propTypes = {
  event: PropTypes.shape({
    scheduledLesson: PropTypes.object.isRequired,
    calendarContext: PropTypes.shape({
      students: PropTypes.array.isRequired,
      teachers: PropTypes.array.isRequired,
      onSubmit: PropTypes.func.isRequired,
      deleteLessonRegister: PropTypes.func.isRequired,
      userRole: PropTypes.string,
      userTeacherId: PropTypes.string,
    }).isRequired,
  }).isRequired,
};

function DashboardCalendar({
  scheduledClasses,
  students,
  teachers,
  onSubmit,
  deleteLessonRegister,
  date,
  view,
  onViewChange,
  onVisibleDateChange,
  userRole,
  userTeacherId,
  lessonTypes,
  creationMode,
  onSelectCreationSlot,
  onCalendarScrollChange,
  restoreScrollTop,
  restorePageScrollY,
  highlightClassId,
}) {
  const container = useRef(null);
  const restored = useRef(false);
  const scrollListener = useRef(onCalendarScrollChange);
  scrollListener.current = onCalendarScrollChange;
  const [scrollToTime] = useState(() => (restoreScrollTop !== null ? moment().startOf('day').toDate() : new Date()));
  useEffect(() => {
    const grid = container.current && container.current.querySelector('.rbc-time-content');
    const onScroll = () => scrollListener.current(grid.scrollTop);
    if (grid) grid.addEventListener('scroll', onScroll);
    const frame = requestAnimationFrame(() => {
      if (!restored.current && (grid || view === 'month')) {
        if (grid && restoreScrollTop !== null) grid.scrollTop = restoreScrollTop;
        if (restorePageScrollY !== null) window.scrollTo(0, restorePageScrollY);
        restored.current = true;
      }
      if (grid) onScroll();
    });
    return () => {
      cancelAnimationFrame(frame);
      if (grid) grid.removeEventListener('scroll', onScroll);
    };
  }, [view, restoreScrollTop, restorePageScrollY]);
  const lessonTypesSetting = normalizeLessonTypesSetting(lessonTypes);
  const events = scheduledClasses.flatMap(scheduledClass => (
    scheduledClass.scheduledLessons.map((scheduledLesson) => {
      const lessonDetails = {
        ...scheduledLesson.defaultLessonDetails,
        ...((scheduledLesson.lessonRegister && scheduledLesson.lessonRegister.lessonDetails) || {}),
      };
      const lessonType = getLessonTypeById(lessonTypesSetting, lessonDetails.classTypeId);
      const duration = lessonType.durationMinutes || 60;
      const start = moment(
        `${lessonDetails.date} ${lessonDetails.startTime}`,
        "YYYY-MM-DD HH:mm",
      ).toDate();
      const end = moment(start).add(duration, "minutes").toDate();

      return {
        id: scheduledLesson.id,
        schoolClassId: scheduledClass.schoolClassId || scheduledLesson.schoolClassId,
        title: lessonType.name || `${lessonDetails.classTypeId}`,
        start,
        end,
        scheduledLesson,
        calendarContext: {
          students,
          teachers,
          onSubmit,
          deleteLessonRegister,
          userRole,
          userTeacherId,
        },
      };
    })
  ));

  const dayStart = moment().hours(8).minutes(0).toDate();
  const dayEnd = moment().hours(20).minutes(0).toDate();
  const eventPropGetter = ({ scheduledLesson }) => {
    if (isLessonCancelled(scheduledLesson)) {
      return {
        className: "dashboard-cancelled-event",
        style: { backgroundColor: CANCELLED_EVENT_BACKGROUND },
      };
    }

    if (hasAllParticipantsCancelled(scheduledLesson)) {
      return {
        className: "dashboard-partially-cancelled-event",
        style: { backgroundColor: PARTIALLY_CANCELLED_EVENT_BACKGROUND },
      };
    }

    return isAttendanceChecked(scheduledLesson)
      ? {
        className: "dashboard-attended-event",
        style: { backgroundColor: ATTENDED_EVENT_BACKGROUND },
      }
      : {};
  };

  return (
    <div ref={container} className={`my-calendar${creationMode ? ' dashboard-creation-mode' : ''}`}>
      <Calendar
        localizer={calendarLocalizer}
        culture="pl"
        messages={calendarMessages}
        formats={calendarFormats}
        events={events}
        views={views}
        onNavigate={onVisibleDateChange}
        onView={onViewChange}
        date={date}
        view={view}
        startAccessor="start"
        endAccessor="end"
        style={{ height: 750 }}
        min={dayStart}
        max={dayEnd}
        step={15}
        timeslots={4}
        scrollToTime={scrollToTime}
        selectable={creationMode ? 'ignoreEvents' : false}
        onSelecting={({ start, end }) => end - start <= 15 * 60 * 1000}
        onSelectSlot={(slot) => {
          if (!creationMode || (slot.slots && slot.slots.length > (view === 'month' ? 1 : 2))) return;
          onSelectCreationSlot({ start: slot.start });
        }}
        slotPropGetter={value => (creationMode ? {
          'data-creation-time': `${WEEKDAYS.find(day => day.day === moment(value).day()).name}, ${moment(value).format('HH:mm')}`,
        } : {})}
        eventPropGetter={(event) => {
          const appearance = eventPropGetter(event);
          return highlightClassId && event.schoolClassId === highlightClassId
            ? { ...appearance, className: `${appearance.className || ''} dashboard-created-event`.trim() }
            : appearance;
        }}
        components={{
          event: DashboardCalendarEvent,
        }}
      />
    </div>
  );
}

DashboardCalendar.propTypes = {
  creationMode: PropTypes.bool,
  onSelectCreationSlot: PropTypes.func,
  onCalendarScrollChange: PropTypes.func,
  restoreScrollTop: PropTypes.number,
  restorePageScrollY: PropTypes.number,
  highlightClassId: PropTypes.string,
  scheduledClasses: PropTypes.array.isRequired,
  students: PropTypes.array.isRequired,
  teachers: PropTypes.array.isRequired,
  onSubmit: PropTypes.func.isRequired,
  deleteLessonRegister: PropTypes.func.isRequired,
  date: PropTypes.instanceOf(Date),
  view: PropTypes.string,
  onViewChange: PropTypes.func,
  onVisibleDateChange: PropTypes.func,
  userRole: PropTypes.string,
  userTeacherId: PropTypes.string,
  lessonTypes: PropTypes.shape({
    id: PropTypes.string,
    types: PropTypes.array,
  }),
};

DashboardCalendar.defaultProps = {
  creationMode: false,
  onSelectCreationSlot: () => {},
  onCalendarScrollChange: () => {},
  restoreScrollTop: null,
  restorePageScrollY: null,
  highlightClassId: null,
  date: new Date(),
  view: "week",
  onViewChange: () => {},
  onVisibleDateChange: () => {},
  userRole: "",
  userTeacherId: "",
  lessonTypes: DEFAULT_LESSON_TYPES_SETTING,
};

export default DashboardCalendar;
